import "twin.macro";

import { formatLocalizedString, getLocalizedString } from "@glide/localization";
import { isUploadFileResponseError } from "@glide/common-core/dist/js/components/types";
import { getAppFacilities } from "@glide/common-core/dist/js/support/app-renderer";
import { ErrorBoundary, getGlideIcon, getMonotoneGlideIcon } from "@glide/common";
import {
    ImagePickerSourceHint,
    TextComponentStyle,
    resizeImageIfNeeded,
    ImageSizePreset,
    getDimensionsForPreset,
} from "@glide/component-utils";
import { assert } from "@glideapps/ts-necessities";

import type { WireBackendInterface } from "@glide/hydrated-ui";
import { ConcurrencyLimiterWithBackpressure, Watchable, ignore, isNonNullDefined } from "@glide/support";
import { UIButtonAppearance, ValueChangeSource, type WireEditableValue } from "@glide/wire";
import { definedMap } from "collection-utils";
import isEmpty from "lodash/isEmpty";
import * as React from "react";
import { lazy } from "react";

import { Text } from "../../components/text/text";
import { WireButton } from "../wire-button/wire-button";
import { useFileUploader } from "../../utils/use-file-uploader";
import { useIsHoverable } from "@glide/common-components";
import type { PickerImage } from "./sortable-image-list";
import { useFeatureSettings } from "@glide/common-core/dist/js/use-feature-settings";

const SortableImageList = lazy(() => import("./sortable-image-list"));

interface MultipleImagePickerProps {
    readonly title: string | undefined;
    readonly editableValue: WireEditableValue<readonly string[]>;
    readonly isRequired: boolean;
    readonly backend: WireBackendInterface;
    readonly sourceHint: ImagePickerSourceHint;
    readonly imageSizePreset?: ImageSizePreset;
}

export const MultipleImagePicker: React.VFC<MultipleImagePickerProps> = p => {
    const { title, editableValue, backend, isRequired, sourceHint, imageSizePreset } = p;

    const { value, onChangeToken } = editableValue;

    const { images, uploadImages, reorderImages } = usePickerImages(value, onChangeToken, backend, imageSizePreset);
    const hasImages = !isEmpty(images);

    const hasTitleElement = isNonNullDefined(title) || isRequired;

    return (
        <div onClick={e => e.stopPropagation()}>
            {hasTitleElement && (
                <div tw="flex mb-2 justify-between">
                    {isNonNullDefined(title) ? (
                        <Text
                            element="h3"
                            variant={TextComponentStyle.small}
                            tw="font-semibold text-text-contextual-dark">
                            {title}
                        </Text>
                    ) : (
                        <div />
                    )}
                    {isRequired && (
                        <Text element="span" variant={TextComponentStyle.footnote}>
                            {getLocalizedString("required")}
                        </Text>
                    )}
                </div>
            )}
            {hasImages ? (
                <ImageList
                    images={images}
                    uploadImages={uploadImages}
                    reorderImages={reorderImages}
                    appID={backend.appID}
                    sourceHint={sourceHint}
                />
            ) : (
                <EmptyState uploadImages={uploadImages} sourceHint={sourceHint} />
            )}
        </div>
    );
};

function getImagesCountFromDataTransferItems(items: DataTransferItemList): number {
    let images = 0;
    for (const item of items) {
        if (item.kind === "file" && item.type.startsWith("image/")) {
            images++;
        }
    }

    return images;
}

function getImagesFromFileList(files: FileList | null): File[] {
    if (files === null) {
        return [];
    }

    const images = [];
    for (const file of files) {
        if (file.type.startsWith("image/")) {
            images.push(file);
        }
    }
    return images;
}

interface UploadInputProps {
    readonly uploadImages: (files: File[] | null) => Promise<void>;
    readonly inputRef?: React.RefObject<HTMLInputElement>;
    readonly sourceHint: ImagePickerSourceHint;
}

const UploadInput: React.VFC<UploadInputProps> = p => {
    const { uploadImages, inputRef, sourceHint } = p;
    const capture = sourceHint === ImagePickerSourceHint.CameraOnly ? "environment" : undefined;

    return (
        <input
            ref={inputRef}
            data-testid="upload-images-input"
            tw="absolute w-0 h-0 invisible"
            type="file"
            multiple={true}
            accept="image/*"
            onChange={ev => uploadImages(getImagesFromFileList(ev.target.files))}
            capture={capture}
        />
    );
};

interface DragAndDropHandlers {
    handlers: {
        onDragOver: (e: React.DragEvent) => void;
        onDrop: (e: React.DragEvent) => void;
        onDragLeave: () => void;
    };
    isDraggingOver: boolean;
    draggedImages: number;
}

function useDragAndDropImages(uploadImages: (files: File[] | null) => Promise<void>): DragAndDropHandlers {
    const [isDraggingOver, setIsDraggingOver] = React.useState(false);
    const [draggedImages, setDraggedImages] = React.useState(0);

    const onDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "copy";

        const imagesCount = getImagesCountFromDataTransferItems(e.dataTransfer.items);
        setDraggedImages(imagesCount);
        setIsDraggingOver(imagesCount > 0);
    };

    const onDrop = (e: React.DragEvent) => {
        e.stopPropagation();
        e.preventDefault();

        const files = e.dataTransfer.files;
        const images = getImagesFromFileList(files);

        void uploadImages(images);
        setIsDraggingOver(false);
    };

    const onDragLeave = () => {
        setIsDraggingOver(false);
    };

    return {
        handlers: {
            onDragOver,
            onDrop,
            onDragLeave,
        },
        isDraggingOver,
        draggedImages,
    };
}

interface EmptyStateProps {
    readonly uploadImages: (files: File[] | null) => Promise<void>;
    readonly sourceHint: ImagePickerSourceHint;
}

const EmptyState: React.VFC<EmptyStateProps> = p => {
    const { uploadImages, sourceHint } = p;

    const { handlers, isDraggingOver, draggedImages } = useDragAndDropImages(uploadImages);

    const isCameraOnly = sourceHint === ImagePickerSourceHint.CameraOnly;

    const isTouchDevice = !useIsHoverable();

    const labelForMainUploadButton =
        isCameraOnly && isTouchDevice
            ? getLocalizedString("takeAPicture")
            : formatLocalizedString("uploadNImages", [""]);

    const iconForMainUploadButton =
        isCameraOnly && isTouchDevice
            ? getMonotoneGlideIcon("mt-page-camera-upload")
            : getMonotoneGlideIcon("mt-page-image-upload");

    return (
        <div {...handlers}>
            <label
                className={isDraggingOver ? "dragging-over" : undefined}
                tw="w-full flex rounded-lg border border-border-dark py-4 px-6 relative cursor-pointer transition-colors [&.dragging-over]:bg-n50 page-hover:bg-n50">
                <UploadInput uploadImages={uploadImages} sourceHint={sourceHint} />
                <div tw="absolute inset-0 pointer-events-none flex justify-center items-center opacity-0 [.dragging-over &]:opacity-100">
                    <Text element="p" variant={TextComponentStyle.regular}>
                        {formatLocalizedString("uploadNImages", [draggedImages.toString()])}
                    </Text>
                </div>
                <div tw="w-full flex flex-col items-center pointer-events-none transition-opacity [.dragging-over &]:opacity-0">
                    <Text element="p" variant={TextComponentStyle.regular} tw="mb-2 mt-1 hidden page-md:block">
                        {getLocalizedString("dragImagesHereOr")}
                    </Text>
                    <WireButton appearance={UIButtonAppearance.Transparent} iconName={iconForMainUploadButton}>
                        {labelForMainUploadButton}
                    </WireButton>
                </div>
            </label>
        </div>
    );
};

interface UsePickerImages {
    readonly images: readonly PickerImage[];
    readonly uploadImages: (files: File[] | null) => Promise<void>;
    readonly reorderImages: (oldIndex: number, newIndex: number) => void;
}

function usePickerImages(
    backendImages: readonly string[],
    onChangeToken: string | undefined,
    backend: WireBackendInterface,
    imageSizePreset?: ImageSizePreset
): UsePickerImages {
    const uploadAppFile = useFileUploader();
    const [resizeImages] = useFeatureSettings(["clientSideImageResizing"]);

    const pickerImagesFromBackend = backendImages.map((i): PickerImage => {
        const onRemove = definedMap(onChangeToken, t => () => {
            const newImages = backendImages.filter(image => image !== i);
            backend.valueChanged(t, newImages, ValueChangeSource.User);
        });

        return {
            key: i,
            isLoading: false,
            loadingPercentage: new Watchable<number>(1),
            src: i,
            onRemove,
        };
    });

    const [pickerImagesLoading, setPickerImagesLoading] = React.useState<PickerImage[]>([]);

    const images = React.useMemo(() => {
        return [...pickerImagesFromBackend, ...pickerImagesLoading];
    }, [pickerImagesFromBackend, pickerImagesLoading]);

    const imagesRef = React.useRef(backendImages);
    imagesRef.current = backendImages;

    const uploadImages = React.useCallback(
        async (fileList: File[] | null) => {
            if (fileList === null || isEmpty(fileList) || onChangeToken === undefined) {
                return;
            }

            const uploadSessions = await Promise.all(
                [...fileList].map(async (file, i) => {
                    let fileToUpload = file;
                    const effectivePreset = imageSizePreset ?? ImageSizePreset.High;
                    if (resizeImages && effectivePreset !== ImageSizePreset.Original) {
                        const presetDims = getDimensionsForPreset(effectivePreset);
                        assert(presetDims !== undefined);
                        const opts = {
                            maxWidth: presetDims.maxWidth,
                            maxHeight: presetDims.maxHeight,
                            quality: 0.85,
                        };
                        fileToUpload = await resizeImageIfNeeded(file, opts);
                    }

                    const loadingPercentage = new Watchable(0);
                    const session = uploadAppFile(
                        backend.appID,
                        getAppFacilities(),
                        fileToUpload,
                        "image-picker",
                        (totalBytes, bytesSent) => {
                            if (totalBytes === 0) {
                                loadingPercentage.current = 0;
                                return;
                            }
                            loadingPercentage.current = bytesSent / totalBytes;
                        },
                        ignore,
                        false
                    );

                    const key = i + fileToUpload.name;
                    const src = URL.createObjectURL(fileToUpload);

                    const onRemove = () => {
                        session.cancel();
                        setPickerImagesLoading(removingCurrent => removingCurrent.filter(c => c.key !== key));
                        URL.revokeObjectURL(src);
                    };

                    const pickerImage: PickerImage = {
                        key,
                        src,
                        isLoading: true,
                        loadingPercentage,
                        onRemove,
                    };

                    return {
                        session,
                        pickerImage,
                    };
                })
            );

            setPickerImagesLoading(current => {
                const newImages = uploadSessions.map(s => s.pickerImage);
                return [...current, ...newImages];
            });

            const limiter = new ConcurrencyLimiterWithBackpressure(5);
            void limiter.forEach(uploadSessions, async s => {
                const { session, pickerImage } = s;

                const response = await session.attempt();

                if (!isUploadFileResponseError(response)) {
                    backend.valueChanged(onChangeToken, [...imagesRef.current, response.path], ValueChangeSource.User);
                }

                pickerImage.onRemove?.();
            });
        },
        [backend, onChangeToken, uploadAppFile, imageSizePreset, resizeImages]
    );

    const reorderImages = React.useCallback(
        (oldIndex: number, newIndex: number) => {
            if (oldIndex === newIndex || onChangeToken === undefined) {
                return;
            }

            const newImages = [...imagesRef.current];
            const image = newImages[oldIndex];
            newImages.splice(oldIndex, 1);
            newImages.splice(newIndex, 0, image);

            backend.valueChanged(onChangeToken, newImages, ValueChangeSource.User);
        },
        [onChangeToken, backend]
    );

    return {
        images,
        uploadImages,
        reorderImages,
    };
}

interface ImageListProps {
    readonly images: readonly PickerImage[];
    readonly uploadImages: (files: File[] | null) => Promise<void>;
    readonly reorderImages: (oldIndex: number, newIndex: number) => void;
    readonly appID: string;
    readonly sourceHint: ImagePickerSourceHint;
}

const ImageList: React.VFC<ImageListProps> = p => {
    const { images, uploadImages, reorderImages, appID, sourceHint } = p;

    const { handlers, isDraggingOver, draggedImages } = useDragAndDropImages(uploadImages);

    const refs = React.useMemo(() => {
        return images.map(() => {
            return React.createRef<HTMLDivElement>();
        });
    }, [images]);

    React.useEffect(() => {
        const removalCallbacks: (() => void)[] = [];

        for (let i = 0; i < images.length; i++) {
            const image = images[i];
            const imageRef = refs[i];

            const updateHandler = (newValue: number) => {
                if (imageRef.current === null) {
                    return;
                }

                imageRef.current.style.transform = `translateX(${newValue * 100}%)`;
            };
            image.loadingPercentage.subscribe(updateHandler);
            updateHandler(image.loadingPercentage.current);

            removalCallbacks.push(() => {
                image.loadingPercentage.unsubscribe(updateHandler);
            });
        }

        return () => {
            for (const removal of removalCallbacks) {
                removal();
            }
        };
    }, [images, refs]);

    const inputRef = React.useRef<HTMLInputElement>(null);

    const triggerUpload = () => {
        inputRef.current?.click();
    };

    return (
        <>
            <div
                {...handlers}
                className={isDraggingOver ? "dragging-over" : undefined}
                tw="w-full relative rounded-lg border border-border-base py-4 px-6 cursor-pointer transition-all page-hover:bg-n50"
                onClick={triggerUpload}>
                <div tw="absolute inset-0 pointer-events-none flex justify-center items-center opacity-0 rounded-lg transition-all [.dragging-over &]:opacity-100 backdrop-blur-2xl z-10">
                    <Text element="p" variant={TextComponentStyle.regular}>
                        {formatLocalizedString("addNImages", [draggedImages.toString()])}
                    </Text>
                </div>
                <ErrorBoundary>
                    <React.Suspense fallback={<div />}>
                        <SortableImageList images={images} refs={refs} appID={appID} onSortEnd={reorderImages} />
                    </React.Suspense>
                </ErrorBoundary>
            </div>
            <WireButton
                onClick={triggerUpload}
                appearance={UIButtonAppearance.MinimalPrimary}
                iconName={getGlideIcon("st-plus-add")}
                tw="py-1! my-0.5"
                iconPlacement="right">
                {getLocalizedString("add")}
            </WireButton>
            <UploadInput uploadImages={uploadImages} inputRef={inputRef} sourceHint={sourceHint} />
        </>
    );
};
